import { useMemo, useState } from 'react';
import { FileSpreadsheet, FileText, CheckCircle2, AlertTriangle, ClipboardCopy, Loader2 } from 'lucide-react';
import { useToast } from '../ui/Toast';
import { lohnBuchungsbelegApi, fmtMoney } from '../../services/lohnBuchungsbelegApi';
import { buildJournal } from '../../services/lohnJournal';
import { bcPasteDifferenceRappen, buildBcClipboard, copyToClipboard } from '../../services/lohnBcExport';
import { trackFeature } from '../../services/usage';
import type { RunDetail } from '../../types/lohnBuchungsbeleg';

interface Props { detail: RunDetail; }

/**
 * Buchungsbeleg (journal) tab: the derived booking entries — one aggregated Soll or
 * Haben posting per account × cost center, sorted by account then cost center, with
 * Σ Soll / Σ Haben totals and a balance indicator. Derived client-side from the
 * voucher (matches the server-generated Excel/PDF export exactly).
 *
 * Σ Soll = Σ Haben holds by construction, so it proves nothing. The two banners that *do*
 * carry signal: the Lohnverrechnungskonto must net to zero, and no raw Betrag may be negative
 * (the journal posts |cell| and takes its direction from the template's Soll/Haben accounts).
 */
export function JournalTab({ detail }: Props) {
    const { toast } = useToast();
    const [busy, setBusy] = useState(false);
    const [copyBusy, setCopyBusy] = useState(false);
    const journal = useMemo(() => buildJournal(detail.voucher), [detail.voucher]);
    const clearingOffen = Math.abs(journal.clearingNet) >= 0.005;
    const negativeRawRows = detail.voucher.negativeRawRowCount ?? 0;

    const download = async (format: 'xlsx' | 'pdf') => {
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.downloadJournalExport(detail.run.id, format, detail.run.period);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Download fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    // Copy the booking entries in the configurable BC "Fibu Erfassungsjournale" column layout
    // (tab-separated) so they paste straight into BC. Layout is fetched lazily so it always
    // reflects the latest saved config from the BC-Export tab. Not shown on screen (too wide).
    const copyBc = async () => {
        setCopyBusy(true);
        try {
            // Template comes along for the per-account MWST groups (looked up by Konto).
            const [config, template] = await Promise.all([
                lohnBuchungsbelegApi.getBcExportLayout(),
                lohnBuchungsbelegApi.getTemplate(),
            ]);
            const tsv = buildBcClipboard(detail.voucher, config, detail.run, template.vatAccounts ?? []);
            const ok = await copyToClipboard(tsv);
            if (ok) trackFeature('lohn-buchungsbeleg.bc-copy-journal', { rows: journal.entries.length });
            const diffRappen = bcPasteDifferenceRappen(detail.voucher);
            if (ok && diffRappen !== 0) {
                toast(`Kopiert, aber Σ Soll und Σ Haben weichen nach Rundung um ${fmtMoney(diffRappen / 100, false)} ab — vor dem Buchen in BC prüfen.`, 'error');
            } else {
                toast(ok ? `${journal.entries.length} Buchungszeilen ins BC-Format kopiert` : 'Kopieren fehlgeschlagen', ok ? 'success' : 'error');
            }
        } catch (e) {
            toast(e instanceof Error ? e.message : 'BC-Layout konnte nicht geladen werden', 'error');
        } finally { setCopyBusy(false); }
    };

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-slate-800">Buchungsbeleg {detail.run.period}</h2>
                    <span className="text-xs text-slate-500">{journal.entries.length} Buchungssätze</span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={copyBc} disabled={copyBusy} title="Buchungssätze im BC-Spaltenlayout in die Zwischenablage kopieren (zum Einfügen in «Fibu Erfassungsjournale»)" className="inline-flex items-center gap-1 rounded bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:opacity-50">
                        {copyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCopy className="h-4 w-4" />} In Zwischenablage (BC)
                    </button>
                    <button onClick={() => download('xlsx')} disabled={busy} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                        <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
                    </button>
                    <button onClick={() => download('pdf')} disabled={busy} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                        <FileText className="h-4 w-4 text-rose-600" /> PDF
                    </button>
                </div>
            </div>

            <div className={`mb-3 flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${journal.balanced ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
                {journal.balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                <span className="font-medium">
                    Σ Soll <span className="font-mono">{fmtMoney(journal.totalSoll, false)}</span>
                    {' · '}Σ Haben <span className="font-mono">{fmtMoney(journal.totalHaben, false)}</span>
                </span>
                <span className="text-xs">
                    {journal.balanced ? 'Beleg geht auf (Σ Soll = Σ Haben).' : `Differenz ${fmtMoney(journal.difference, false)} — Beleg geht nicht auf.`}
                </span>
            </div>

            {clearingOffen && (
                <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        <span className="font-medium">Lohnverrechnungskonto {journal.clearingAccount} geht nicht auf: </span>
                        <span className="font-mono">{fmtMoney(journal.clearingNet, false)}</span>
                        <span className="text-xs">
                            {' '}— Bruttolohn ≠ Abzüge + Nettolohn. Der Beleg enthält deshalb eine Zeile auf Konto {journal.clearingAccount}.
                            Geht das Konto auf, verschwindet sie automatisch.
                        </span>
                    </span>
                </div>
            )}

            {negativeRawRows > 0 && (
                <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        <span className="font-medium">{negativeRawRows} Rohzeile{negativeRawRows === 1 ? '' : 'n'} mit negativem Betrag.</span>
                        <span className="text-xs">
                            {' '}Die Buchungsrichtung stammt aus Soll/Haben der Vorlage — negative Rohbeträge können sie verfälschen. Bitte prüfen.
                        </span>
                    </span>
                </div>
            )}

            <p className="mb-3 text-xs text-slate-500">
                Je Konto und Kostenstelle ein aggregierter Soll- bzw. Haben-Eintrag — genau das, was in die Fibu gebucht wird.
                Abgeleitet aus der Zuordnungs-Matrix (inkl. Overrides). Bilanzkonten (1…, 2…, 9…) buchen ohne Kostenstelle.
            </p>

            {journal.entries.length === 0 ? (
                <div className="rounded border border-dashed border-slate-200 px-3 py-10 text-center text-sm text-slate-400">
                    Keine Buchungssätze — die Zuordnung enthält keine Beträge.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-100 text-slate-600">
                                <th className="px-3 py-1.5 text-right font-semibold">Konto</th>
                                <th className="px-3 py-1.5 text-right font-semibold">Kostenstelle</th>
                                <th className="px-3 py-1.5 text-right font-semibold">Soll</th>
                                <th className="px-3 py-1.5 text-right font-semibold">Haben</th>
                                <th className="px-3 py-1.5 text-left font-semibold">Text</th>
                            </tr>
                        </thead>
                        <tbody>
                            {journal.entries.map((e, i) => (
                                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60">
                                    <td className="px-3 py-1 text-right font-mono text-slate-700">{e.account}</td>
                                    <td className="px-3 py-1 text-right font-mono text-slate-500">{e.costCenter || '—'}</td>
                                    <td className="px-3 py-1 text-right font-mono tabular-nums text-slate-800">{e.soll ? fmtMoney(e.soll, false) : ''}</td>
                                    <td className="px-3 py-1 text-right font-mono tabular-nums text-slate-800">{e.haben ? fmtMoney(e.haben, false) : ''}</td>
                                    <td className="px-3 py-1 text-slate-500">{e.text}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold text-slate-700">
                                <td className="px-3 py-1.5" colSpan={2}>Total</td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtMoney(journal.totalSoll, false)}</td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtMoney(journal.totalHaben, false)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
